import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import { SuppressedValue } from '@/components/data/SuppressedValue'
import { Banner } from '@/components/feedback/Banner'
import { ErrorState } from '@/components/feedback/ErrorState'
import { Skeleton } from '@/components/feedback/Skeleton'
import { Toast } from '@/components/feedback/Toast'
import { PageHeader } from '@/components/layout/PageHeader'
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card'
import { CountryText } from '@/components/ui/CountryText'
import { StatusPill } from '@/components/ui/StatusPill'
import { useClientStatement } from '@/features/clients/api/useClientStatement'
import { ClientMoneyTiles } from '@/features/clients/components/ClientMoneyTiles'
import { ClientRecordActions } from '@/features/clients/components/ClientRecordActions'
import { ClientStatementTable } from '@/features/clients/components/ClientStatementTable'
import { useYearParam, YearControl } from '@/features/clients/components/YearControl'

/** What the clients page hands over when it navigates here straight after a
 *  registration: the number the server just minted, so the receipt can name
 *  it. Router state rather than a query parameter, because a reload or a
 *  shared link must not replay "registered" for a client registered last week. */
export interface ClientRegisteredState {
  registered: string
}

function registeredNumber(state: unknown): string | undefined {
  if (typeof state !== 'object' || state === null || !('registered' in state)) return undefined
  const value = (state as ClientRegisteredState).registered
  return typeof value === 'string' && value !== '' ? value : undefined
}

/* One client: who they are, what they owe, and every shipment behind it for
 * the chosen year — the workbook's per-customer sheet with its opening line
 * and running balance. Money follows the server's `moneyUnavailable`
 * exactly as the overview does. A 404 covers both a missing client and one
 * outside the caller's scope, and ErrorState words it. */
export function ClientPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { clientId = '' } = useParams()
  const [year, setYear] = useYearParam()
  const { data, isPending, isError, error, refetch } = useClientStatement(clientId, year)
  const registered = registeredNumber(location.state)

  /* The receipt is dismissed by REPLACING the history entry without its state,
     not by a local flag: a flag would reset on the next mount, and Back then
     Forward would announce the registration a second time. */
  const dismissRegistered = () =>
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null })

  if (isPending) {
    return (
      <div className="flex flex-col gap-4" aria-busy="true">
        <Skeleton className="h-24 w-full rounded-[var(--radius-DEFAULT)]" />
        <Skeleton className="h-72 w-full rounded-[var(--radius-DEFAULT)]" />
      </div>
    )
  }
  if (isError) return <ErrorState error={error} onRetry={() => void refetch()} />

  const { client } = data

  return (
    <>
      <PageHeader
        /* The registry number first: it is the identifier every client has
           and the one the documents print. The hand-typed code, when there is
           one, follows it as the secondary id. */
        eyebrow={
          <span className="flex items-center gap-2">
            {t('clients.statement.eyebrow')}
            <span className="font-mono">{client.number}</span>
            {client.code ? <span className="text-text-muted font-mono">{client.code}</span> : null}
          </span>
        }
        title={client.name}
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            <CountryText value={client.country} />
            {client.taxId ? <span>{t('directories.clients.taxId')} {client.taxId}</span> : null}
            <StatusPill tone={client.isActive ? 'positive' : 'neutral'}>
              {client.isActive ? t('common.form.active') : t('common.form.inactive')}
            </StatusPill>
          </span>
        }
        actions={
          <>
            <YearControl years={data.years} value={year} onChange={setYear} label={t('clients.year.label')} />
            <ClientRecordActions client={client} ownerName={data.ownerName} />
          </>
        }
      />

      {data.moneyUnavailable === 'unavailable' ? (
        <Banner tone="attention" title={t('clients.moneyUnavailable.title')}>
          {t('clients.moneyUnavailable.body')}
        </Banner>
      ) : null}

      {data.totals && data.opening ? (
        <ClientMoneyTiles money={data.totals} opening={data.opening.balance} />
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>{t('clients.statement.title')}</CardTitle>
        </CardHeader>
        <ClientStatementTable
          rows={data.rows}
          opening={data.opening?.balance}
          totals={data.totals}
          onOpenCase={(row) => navigate(`/cases/${row.caseId}`)}
        />
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('clients.statement.details')}</CardTitle>
        </CardHeader>
        <CardBody>
          <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-[13px] md:grid-cols-2">
            {/* Two different people, and the difference is the point: who
                brought the client in never changes, while the sales contact is
                whoever is responsible for it now. */}
            <dt className="text-text-muted">{t('clients.people.salesContact')}</dt>
            <dd>{data.ownerName ?? t('common.empty')}</dd>
            <dt className="text-text-muted">{t('clients.people.registeredBy')}</dt>
            <dd>{data.createdByName ?? t('common.empty')}</dd>
            <dt className="text-text-muted">{t('directories.clients.registrationId')}</dt>
            <dd>{client.registrationId || t('common.empty')}</dd>
            <dt className="text-text-muted">{t('directories.clients.address')}</dt>
            <dd>{client.address || t('common.empty')}</dd>
          </dl>
          {client.contacts.length > 0 ? (
            <ul className="mt-3 flex flex-col gap-1.5 text-[13px]">
              {client.contacts.map((contact) => (
                <li key={contact.id} className="flex flex-wrap gap-x-4">
                  <span className="text-text-heading">{contact.label}</span>
                  <SuppressedValue value={contact.email} />
                  <SuppressedValue value={contact.phone} />
                </li>
              ))}
            </ul>
          ) : null}
        </CardBody>
      </Card>

      <Toast
        open={registered !== undefined}
        message={registered ? t('clients.registered', { number: registered }) : ''}
        onDismiss={dismissRegistered}
      />
    </>
  )
}
